import { getApiClient } from '@src/services/api-client'
import { useQuery } from '@tanstack/react-query'
import { CreditDashboardEntry } from '../../types/CreditDashboardEntry'

const BASE_URL = '/CreditDashboardEntries'

// Query keys
export const creditDashboardKeys = {
  all: ['creditDashboardEntries'] as const,
  list: (filters: Record<string, string | number | string[] | number[]> | undefined) =>
    [{ ...creditDashboardKeys.all, filters }] as const,
  detail: (id: string) => [...creditDashboardKeys.all, id] as const,
}

// API functions
async function getCreditDashboardEntriesByFilters(
  filters?: Record<string, string | number | string[] | number[]>,
): Promise<CreditDashboardEntry[]> {
  const apiClient = getApiClient()
  const queryString = filters ? `?${serializeParameters(filters)}` : ''
  const response = await apiClient.get<CreditDashboardEntry[]>(`${BASE_URL}${queryString}`)
  return response.data
}

function serializeParameters(params: Record<string, string | number | string[] | number[]>): string {
  return Object.entries(params)
    .filter(([, value]) => {
      // Filter out null, undefined, empty strings, and empty arrays
      if (value === null || value === undefined || value === '') return false
      if (Array.isArray(value) && value.length === 0) return false
      return true
    })
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return value.map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`).join('&')
      }
      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    })
    .join('&')
}

async function getCreditDashboardEntryById(creditDashboardEntryId: string): Promise<CreditDashboardEntry> {
  const apiClient = getApiClient()
  const response = await apiClient.get<CreditDashboardEntry>(`${BASE_URL}/${creditDashboardEntryId}`)
  return response.data
}

// React Query hooks
export function useGetCreditDashboardEntries(
  filters?: Record<string, string | number | string[] | number[]>,
  enabled = true,
): [CreditDashboardEntry[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: creditDashboardKeys.list(filters),
    queryFn: () => getCreditDashboardEntriesByFilters(filters),
    enabled,
  })
  return [data ?? [], isFetching]
}

export function useGetCreditDashboardEntryById(
  creditDashboardEntryId: string | undefined,
  enabled = true,
): [CreditDashboardEntry | null, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: creditDashboardKeys.detail(creditDashboardEntryId ?? ''),
    queryFn: () => getCreditDashboardEntryById(creditDashboardEntryId!),
    enabled: enabled && !!creditDashboardEntryId,
  })
  return [data ?? null, isFetching]
}
